import { SagaIterator } from 'redux-saga'
import * as effects from 'redux-saga/effects'
import * as api from './api'
import * as actions from './actions'
import { formatErrorMessage } from '../../utils/helpers'
import history from '../../utils/history'
import * as Sentry from '@sentry/react'
import {
  EditLessonType,
  FetchLessonType,
  ConnectModuleLessonType,
  RemoveLessonModuleType,
} from './types'

function* fetchLesson(payload: FetchLessonType): SagaIterator {
  try {
    const { id, lid } = payload
    const lesson = yield effects.call(api.fetchLesson, id, lid)
    yield effects.put(actions.fetchLessonSuccess(lesson.data))
  } catch (error) {
    console.warn('Cannot fetch lesson', error)
    yield effects.put(actions.fetchLessonFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* fetchThemes(payload: { courseId: string }): SagaIterator {
  try {
    const themes = yield effects.call(api.fetchThemes, payload.courseId)
    yield effects.put(actions.fetchThemesSuccess(themes.data))
  } catch (error) {
    console.warn('Cannot fetch themes', error)
    yield effects.put(actions.fetchThemesFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* editLesson(payload: EditLessonType): SagaIterator {
  try {
    const { lid, id, title, icon, theme } = payload
    yield effects.call(api.editLesson, lid, id, title, icon, theme)
    yield effects.put(actions.editLessonSuccess())
    yield effects.call([history, history.push], `/admin/courses/${id}/lessons`)
  } catch (error) {
    console.warn('Cannot edit lesson', error)
    yield effects.put(actions.editLessonFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* fetchModules(payload: { id: string }): SagaIterator {
  try {
    const modules = yield effects.call(api.fetchModules, payload.id)
    yield effects.put(actions.fetchModulesSuccess(modules.data))
  } catch (error) {
    console.warn('Cannot fetch modules', error)
    yield effects.put(actions.fetchModulesFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* connectModuleLesson(payload: ConnectModuleLessonType): SagaIterator {
  try {
    const {
      id,
      mid,
      lid,
      indexNumber,
      assistantCaption,
      timeToStudy,
      isFirst,
      isLast,
      isFree,
      availableAt,
      position,
    } = payload
    yield effects.call(
      api.connectModuleLesson,
      mid,
      lid,
      indexNumber,
      assistantCaption,
      timeToStudy,
      isFirst,
      isLast,
      isFree,
      availableAt,
      position
    )
    yield effects.put(actions.connectModuleLessonSuccess())
    yield effects.put(actions.fetchLesson(id, lid))
  } catch (error) {
    console.warn('Cannot connect module with lesson', error)
    yield effects.put(
      actions.connectModuleLessonFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* editModuleLesson(payload: ConnectModuleLessonType): SagaIterator {
  try {
    const {
      id,
      lid,
      indexNumber,
      assistantCaption,
      moduleLessonId,
      timeToStudy,
      isFirst,
      isLast,
      isFree,
      availableAt,
      position,
    } = payload
    yield effects.call(
      api.editModuleLesson,
      indexNumber,
      assistantCaption,
      moduleLessonId,
      timeToStudy,
      isFirst,
      isLast,
      isFree,
      availableAt,
      position
    )
    yield effects.put(actions.editModuleLessonSuccess())
    yield effects.put(actions.fetchLesson(id, lid))
  } catch (error) {
    console.warn('Cannot edit moduleLesson', error)
    yield effects.put(
      actions.editModuleLessonFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* removeModuleLesson(payload: RemoveLessonModuleType): SagaIterator {
  try {
    const { id, lid, moduleLessonId, isLast } = payload
    yield effects.call(api.removeModuleLesson, moduleLessonId)
    yield effects.put(actions.removeModuleLessonSuccess())
    if (isLast) {
      yield effects.call(
        [history, history.push],
        `/admin/courses/${id}/lessons`
      )
    } else {
      yield effects.put(actions.fetchLesson(id, lid))
      yield effects.put(actions.fetchModules(id))
    }
  } catch (error) {
    console.warn('Cannot remove moduleLesson', error)
    yield effects.put(
      actions.removeModuleLessonFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* createLessonRelation(payload: {
  id: string
  mid: string
  relationType: string
  source: string
  target: string
}): SagaIterator {
  try {
    const { id, mid, relationType, source, target } = payload
    yield effects.call(
      api.createLessonRelation,
      mid,
      relationType,
      source,
      target
    )
    yield effects.put(actions.createLessonRelationSuccess())
    yield effects.put(actions.fetchLesson(id, source))
  } catch (error) {
    console.warn('Cannot create lesson relation', error)
    yield effects.put(
      actions.createLessonRelationFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* editLessonRelation(payload: {
  lessonRelation: string
  id: string
  mid: string
  relationType: string
  source: string
  target: string
}): SagaIterator {
  try {
    const { lessonRelation, id, mid, relationType, source, target } = payload
    yield effects.call(
      api.editLessonRelation,
      lessonRelation,
      mid,
      relationType,
      source,
      target
    )
    yield effects.put(actions.editLessonRelationSuccess())
    yield effects.put(actions.fetchLesson(id, source))
  } catch (error) {
    console.warn('Cannot edit lesson relation', error)
    yield effects.put(
      actions.editLessonRelationFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* removeLessonRelation(payload: {
  lessonRelation: string
  id: string
  source: string
}): SagaIterator {
  try {
    const { lessonRelation, id, source } = payload
    yield effects.call(api.removeLessonRelation, lessonRelation)
    yield effects.put(actions.removeLessonRelationSuccess())
    yield effects.put(actions.fetchLesson(id, source))
  } catch (error) {
    console.warn('Cannot remove lesson relation', error)
    yield effects.put(
      actions.removeLessonRelationFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* watchFetchLesson(): SagaIterator {
  yield effects.takeEvery(actions.fetchLesson.toString(), fetchLesson)
}

function* watchFetchThemes(): SagaIterator {
  yield effects.takeEvery(actions.fetchThemes.toString(), fetchThemes)
}

function* watchEditLesson(): SagaIterator {
  yield effects.takeEvery(actions.editLesson.toString(), editLesson)
}

function* watchFetchModules(): SagaIterator {
  yield effects.takeEvery(actions.fetchModules.toString(), fetchModules)
}

function* watchConnectModuleLesson(): SagaIterator {
  yield effects.takeEvery(
    actions.connectModuleLesson.toString(),
    connectModuleLesson
  )
}

function* watchEditModuleLesson(): SagaIterator {
  yield effects.takeEvery(actions.editModuleLesson.toString(), editModuleLesson)
}

function* watchRemoveModuleLesson(): SagaIterator {
  yield effects.takeEvery(
    actions.removeModuleLesson.toString(),
    removeModuleLesson
  )
}

function* watchCreateLessonRelation(): SagaIterator {
  yield effects.takeEvery(
    actions.createLessonRelation.toString(),
    createLessonRelation
  )
}

function* watchEditLessonRelation(): SagaIterator {
  yield effects.takeEvery(
    actions.editLessonRelation.toString(),
    editLessonRelation
  )
}

function* watchRemoveLessonRelation(): SagaIterator {
  yield effects.takeEvery(
    actions.removeLessonRelation.toString(),
    removeLessonRelation
  )
}

export default function* watchAdminLessonEdit(): SagaIterator {
  yield effects.all([
    effects.fork(watchFetchThemes),
    effects.fork(watchEditLesson),
    effects.fork(watchFetchLesson),
    effects.fork(watchFetchModules),
    effects.fork(watchConnectModuleLesson),
    effects.fork(watchEditModuleLesson),
    effects.fork(watchRemoveModuleLesson),
    effects.fork(watchCreateLessonRelation),
    effects.fork(watchEditLessonRelation),
    effects.fork(watchRemoveLessonRelation),
  ])
}
